import {
  BaseModal,
  ConfirmationPopup,
  CourseInformation,
  CourseKeyDetails,
  CourseLearningObjectives,
  CourseAuthors,
  CourseAcknowledgements,
  ChaptersManagement,
  CourseQuestions,
  BaseSideMenu,
  CourseOverview,
  CoursesIncluded
} from '../components';
import linkIcon from '../assets/link-external.svg';
import { useParams, useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  create_new_course,
  get_course,
  save_course,
  HANDLE_STATUS,
  HANDLE_SERVER_ERR,
  SET_ERR_MSG,
  UPDATE_COURSE,
  HANDLE_NEW_COURSE
} from '../store/modules/course';
import { CLEAR_QUESTIONS, save_questions, get_questions } from '../store/modules/questions';
import { fetch_chapters, SET_CHAPTERS, save_chapters } from '../store/modules/chapters';
import './css/module-edit.scss';
import '../components/css/content-editor.scss';

export const quickLinksData = {
  title: 'Quick Links',
  options: ['Key details', 'Course information', 'Learning objectives', 'Authors']
};
export const actions = {
  title: 'Course actions',
  options: [
    {
      name: 'Create a new course',
      action: 'create',
      cy: 'create-new-course'
    },
    {
      name: 'Update last updated date',
      action: 'update_date'
    },
    {
      name: 'Open live site',
      icon: linkIcon,
      action: 'open_live_site'
    }
  ]
};

const CourseEdit = function () {
  const { id, language = 'en_GB' } = useParams();
  const {
    location: { pathname }
  } = useHistory();
  const [quickLinks, setQuickLinks] = useState(quickLinksData);
  const dispatch = useDispatch();
  const { id: courseId, moduleType } = useSelector((state) => state.course.data);
  const { serverError, status, relatedVidCourses } = useSelector((state) => state.course);
  const hasRelatedVidCourses = moduleType !== 'VID' && relatedVidCourses && relatedVidCourses.length > 0;
  const { questions } = useSelector((state) => state.questions.data);

  useEffect(() => {
    const base4 = quickLinksData.options.slice();
    const baseWithVid = hasRelatedVidCourses
      ? [base4[0], 'Related VID courses', ...base4.slice(1)]
      : base4;
    let array;
    switch (moduleType) {
      case 'LEP':
        courseId && dispatch(fetch_chapters({ id, language }));
        courseId && dispatch(get_questions({ id, language }));
        array = [...baseWithVid, 'Chapters', 'Questions'];
        break;
      case 'MCQ':
        array = [...baseWithVid, 'Questions'];
        break;
      default:
        array = baseWithVid;
    }
    setQuickLinks((prev) => ({
      ...prev,
      options: array
    }));
  }, [moduleType, relatedVidCourses]);

  useEffect(() => {
    // this one should trigger if the course is new
    // if the route contains /new then trigger this
    if (window.location.pathname.includes('/new')) {
      dispatch(HANDLE_NEW_COURSE());
      dispatch(SET_CHAPTERS([]));
      dispatch(CLEAR_QUESTIONS());
    } else {
      dispatch(get_course({ id, language }));
    }
  }, []);

  function handleMenuAction(action) {
    switch (action) {
      case 'create':
        dispatch(HANDLE_NEW_COURSE());
        dispatch(SET_CHAPTERS([]));
        dispatch(CLEAR_QUESTIONS());
        window.history.replaceState({}, '', '/course/new');
        break;
      case 'update_date':
        dispatch(UPDATE_COURSE({ key: 'dateUpdated', value: new Date() }));
        break;
      case 'open_live_site':
        window.open(`https://new-learning.bmj.com${pathname}`, '_blank');
        break;
    }
  }

  function clearServerErr() {
    dispatch(HANDLE_SERVER_ERR(false));
    dispatch(HANDLE_STATUS('idle'));
    dispatch(SET_ERR_MSG(''));
  }

  async function handleSaveAll() {
    if (moduleType === 'LEP') {
      const missingAlt = questions.some(
        (q) => (q.questionImage && !q.questionImageAlt) || (q.answerImage && !q.answerImageAlt)
      );
      if (missingAlt) {
        const errMessage = 'Alt text is required for all uploaded images in questions. Please add alt text before saving.';
        dispatch(SET_ERR_MSG(errMessage));
        dispatch(HANDLE_SERVER_ERR(true));
        return;
      }
    }
    if (courseId) {
      dispatch(save_course());
      if (moduleType === 'LEP') {
        dispatch(save_questions({ id: courseId, language })),
          dispatch(save_chapters({ id: courseId, language }));
      }
      if (moduleType === 'MCQ') {
        dispatch(save_questions({ id: courseId, language }));
      }
    }
    if (!courseId) {
      dispatch(create_new_course({ language }));
    }
  }

  return (
    <main className="module-edit content-editor">
      {serverError && (
        <BaseModal>
          <ConfirmationPopup handleConfirm={clearServerErr} />
        </BaseModal>
      )}

      <BaseSideMenu
        quickLinks={quickLinks}
        status={status}
        actions={actions}
        handleSave={handleSaveAll}
        handleAction={handleMenuAction}
      />

      <CourseKeyDetails />

      {hasRelatedVidCourses && (
        <CoursesIncluded isRelatedVidCourses courses={relatedVidCourses} />
      )}

      <CourseInformation />

      <CourseLearningObjectives id="learning-objectives" />

      {moduleType === 'VID' && <CourseOverview />}

      <CourseAuthors />

      <CourseAcknowledgements />

      {moduleType === 'LEP' && <ChaptersManagement />}

      {(moduleType === 'LEP' || moduleType === 'MCQ') && (
        <CourseQuestions />
      )}
    </main>
  );
};

export default CourseEdit;
